import type WaDropdownItem from '@awesome.me/webawesome-pro/dist/components/dropdown-item/dropdown-item.js'
import type WaInput from '@awesome.me/webawesome-pro/dist/components/input/input.js'
import { filter, find, findIndex, flatMap, forEach, map, pipe } from 'remeda'

const SEARCH_DISABLED_ATTRIBUTE = 'data-search-disabled'
const SEARCH_HIDDEN_ATTRIBUTE = 'data-search-hidden'
const SEARCH_RESULT_ATTRIBUTE = 'data-search-result'
const SEARCH_INPUT_SELECTOR = ':scope > wa-input[data-part="search"]'

type ItemVisibility = Readonly<{
  item: WaDropdownItem
  visible: boolean
}>

type SearchResult = Readonly<{
  disabled: boolean
  item: WaDropdownItem
  path: readonly string[]
  root: WaDropdownItem
}>

type BranchSearch = Readonly<{
  items: readonly ItemVisibility[]
  results: readonly SearchResult[]
}>

type SearchPlan = Readonly<{
  items: readonly ItemVisibility[]
  results: readonly SearchResult[]
}>

type SearchState =
  | Readonly<{ type: 'absent' }>
  | Readonly<{
      cleanup: () => void
      input: WaInput
      navigation: 'idle' | 'item'
      query: string
      type: 'bound'
    }>

export type DropdownSearchController = Readonly<{
  cleanup: () => void
  focus: () => void
  prepareForUpdate: () => void
  reset: () => void
  sync: () => void
}>

const isDropdownItem = (element: Element): element is WaDropdownItem =>
  element.localName === 'wa-dropdown-item'

const directItems = (container: HTMLElement): readonly WaDropdownItem[] =>
  pipe(Array.from(container.children), filter(isDropdownItem))

const dropdownItems = (dropdown: HTMLElement): readonly WaDropdownItem[] =>
  pipe(
    Array.from(dropdown.querySelectorAll<WaDropdownItem>('wa-dropdown-item')),
    filter((item) => item.closest('wa-dropdown') === dropdown)
  )

const isLabelNode = (node: Node): boolean => {
  if (node.nodeType === Node.TEXT_NODE) {
    return true
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false
  }

  const slot = (node as Element).getAttribute('slot')

  return slot === null || slot === ''
}

const normalizeSearchText = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toLocaleLowerCase()

const dropdownItemLabel = (item: HTMLElement): string =>
  pipe(
    Array.from(item.childNodes),
    filter(isLabelNode),
    map((node) => node.textContent ?? '')
  )
    .join(' ')
    .trim()
    .replace(/\s+/g, ' ')

export const dropdownItemSearchLabel = (item: HTMLElement): string =>
  normalizeSearchText(dropdownItemLabel(item))

const evaluateBranch = (
  item: WaDropdownItem,
  query: string,
  ancestorMatched: boolean,
  ancestorPath: readonly string[],
  root: WaDropdownItem,
  ancestorDisabled: boolean,
  ancestorHidden: boolean
): BranchSearch => {
  const label = dropdownItemLabel(item)
  const path = [...ancestorPath, label]
  const disabled = ancestorDisabled || item.hasAttribute('disabled')
  const hidden = ancestorHidden || item.hasAttribute('hidden')
  const branchMatched =
    !hidden && (ancestorMatched || normalizeSearchText(label).includes(query))
  const children = directItems(item)

  if (children.length === 0) {
    return {
      items: [{ item, visible: item === root && branchMatched && !hidden }],
      results:
        item !== root && branchMatched
          ? [{ disabled, item, path, root }]
          : ([] as const)
    }
  }

  const childResults = pipe(
    children,
    map((child) =>
      evaluateBranch(child, query, branchMatched, path, root, disabled, hidden)
    )
  )

  return {
    items: [
      { item, visible: false },
      ...pipe(
        childResults,
        flatMap((result) => result.items)
      )
    ],
    results: pipe(
      childResults,
      flatMap((result) => result.results)
    )
  }
}

const dropdownSearchPlan = (
  dropdown: HTMLElement,
  rawQuery: string
): SearchPlan => {
  const query = normalizeSearchText(rawQuery)

  if (query === '') {
    return {
      items: pipe(
        dropdownItems(dropdown),
        map((item) => ({ item, visible: true }))
      ),
      results: []
    }
  }

  const branches = pipe(
    directItems(dropdown),
    map((item) => evaluateBranch(item, query, false, [], item, false, false))
  )

  return {
    items: pipe(
      branches,
      flatMap((result) => result.items)
    ),
    results: pipe(
      branches,
      flatMap((result) => result.results)
    )
  }
}

export const dropdownSearchVisibility = (
  dropdown: HTMLElement,
  rawQuery: string
): readonly ItemVisibility[] => dropdownSearchPlan(dropdown, rawQuery).items

export const dropdownSearchResults = (
  dropdown: HTMLElement,
  rawQuery: string
): readonly SearchResult[] => dropdownSearchPlan(dropdown, rawQuery).results

const removeSearchResults = (dropdown: HTMLElement): void => {
  const results = dropdown.querySelectorAll<WaDropdownItem>(
    `wa-dropdown-item[${SEARCH_RESULT_ATTRIBUTE}]`
  )

  pipe(
    Array.from(results),
    filter((item) => item.closest('wa-dropdown') === dropdown),
    forEach((item) => item.remove())
  )
}

const closeSubmenu = (item: WaDropdownItem): void => {
  item.submenuOpen = false
}

const restoreItem = (item: WaDropdownItem): void => {
  if (item.hasAttribute(SEARCH_HIDDEN_ATTRIBUTE)) {
    item.removeAttribute('hidden')
    item.removeAttribute(SEARCH_HIDDEN_ATTRIBUTE)
  }

  if (item.hasAttribute(SEARCH_DISABLED_ATTRIBUTE)) {
    item.removeAttribute('disabled')
    item.setAttribute('aria-disabled', 'false')
    item.removeAttribute(SEARCH_DISABLED_ATTRIBUTE)
  }
}

const restoreItems = (dropdown: HTMLElement): void => {
  pipe(dropdownItems(dropdown), forEach(restoreItem))
}

const hideItem = (item: WaDropdownItem): void => {
  if (!item.hasAttribute('hidden')) {
    item.setAttribute('hidden', '')
    item.setAttribute(SEARCH_HIDDEN_ATTRIBUTE, '')
  }

  if (!item.hasAttribute('disabled')) {
    item.setAttribute('disabled', '')
    item.setAttribute(SEARCH_DISABLED_ATTRIBUTE, '')
  }

  item.active = false
  closeSubmenu(item)
}

const cloneSlotElements = (
  item: WaDropdownItem,
  slot: 'details' | 'icon'
): readonly Element[] =>
  pipe(
    Array.from(item.children),
    filter((child) => child.getAttribute('slot') === slot),
    map((child) => child.cloneNode(true) as Element)
  )

const createSearchResult = (result: SearchResult): WaDropdownItem => {
  const item = document.createElement('wa-dropdown-item') as WaDropdownItem
  const breadcrumb = document.createElement('span')
  const path = document.createElement('span')
  const separator = document.createElement('span')
  const label = document.createElement('span')
  const parentPath = result.path.slice(0, -1).join(' › ')

  item.setAttribute(SEARCH_RESULT_ATTRIBUTE, '')
  item.setAttribute('aria-label', result.path.join(' › '))
  breadcrumb.dataset.part = 'search-breadcrumb'
  path.dataset.part = 'search-path'
  path.textContent = parentPath
  separator.dataset.part = 'search-separator'
  separator.setAttribute('aria-hidden', 'true')
  separator.textContent = '›'
  label.dataset.part = 'search-label'
  label.textContent = result.path[result.path.length - 1] ?? ''

  pipe(
    ['checked', 'type', 'variant'],
    forEach((attribute) => {
      const value = result.item.getAttribute(attribute)

      if (value !== null) {
        item.setAttribute(attribute, value)
      }
    })
  )

  if (result.disabled) {
    item.setAttribute('disabled', '')
  }

  breadcrumb.append(path, separator, label)
  item.append(
    ...cloneSlotElements(result.item, 'icon'),
    breadcrumb,
    ...cloneSlotElements(result.item, 'details')
  )

  return item
}

const renderSearchResults = (
  results: readonly SearchResult[]
): ReadonlyMap<HTMLElement, WaDropdownItem> => {
  const rendered = pipe(
    results,
    map((result) => ({
      element: createSearchResult(result),
      result
    }))
  )

  pipe(
    rendered,
    forEach(({ element, result }) => result.root.before(element))
  )

  return new Map(
    pipe(
      rendered,
      map(({ element, result }) => [element, result.item] as const)
    )
  )
}

const applySearch = (
  dropdown: HTMLElement,
  query: string
): ReadonlyMap<HTMLElement, WaDropdownItem> => {
  removeSearchResults(dropdown)
  restoreItems(dropdown)

  const plan = dropdownSearchPlan(dropdown, query)
  const resultSources = renderSearchResults(plan.results)

  pipe(
    plan.items,
    forEach(({ item, visible }) => {
      if (!visible) {
        hideItem(item)
      }
    })
  )

  return resultSources
}

const closeSubmenus = (dropdown: HTMLElement): void => {
  pipe(dropdownItems(dropdown), forEach(closeSubmenu))
}

const searchInput = (dropdown: HTMLElement): WaInput | undefined => {
  const input = dropdown.querySelector(SEARCH_INPUT_SELECTOR)

  return input?.localName === 'wa-input' ? (input as WaInput) : undefined
}

const searchInputValue = (event: Event, input: WaInput): string => {
  const source = event.composedPath()[0]
  const view = input.ownerDocument.defaultView

  return view !== null && source instanceof view.HTMLInputElement
    ? source.value
    : (input.value ?? '')
}

const isEnabledVisibleItem = (item: WaDropdownItem): boolean =>
  !(item.hidden || item.disabled || item.hasAttribute('disabled'))

const clearActiveItems = (dropdown: HTMLElement): void => {
  pipe(
    dropdownItems(dropdown),
    forEach((item) => {
      item.active = false
    })
  )
}

const moveActiveItem = (
  dropdown: HTMLElement,
  direction: 'next' | 'previous'
): WaDropdownItem | undefined => {
  const items = pipe(directItems(dropdown), filter(isEnabledVisibleItem))

  if (items.length === 0) {
    return undefined
  }

  const activeIndex = pipe(
    items,
    findIndex((item) => item.active)
  )
  const targetIndex =
    activeIndex === -1
      ? direction === 'next'
        ? 0
        : items.length - 1
      : (activeIndex + (direction === 'next' ? 1 : -1) + items.length) %
        items.length
  const target = items[targetIndex]

  pipe(
    directItems(dropdown),
    forEach((item) => {
      item.active = item === target
    })
  )
  target?.scrollIntoView?.({ block: 'nearest' })

  return target
}

const activeVisibleItem = (dropdown: HTMLElement): WaDropdownItem | undefined =>
  pipe(
    dropdownItems(dropdown),
    find((item) => item.active && isEnabledVisibleItem(item))
  )

const handleSearchKeydown = (
  dropdown: HTMLElement,
  event: KeyboardEvent,
  navigation: 'idle' | 'item'
): 'idle' | 'item' => {
  if (event.key === 'Escape' || event.key === 'Tab') {
    return navigation
  }

  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    event.stopPropagation()
    const target = moveActiveItem(
      dropdown,
      event.key === 'ArrowDown' ? 'next' : 'previous'
    )

    return target === undefined ? 'idle' : 'item'
  }

  event.stopPropagation()

  if (event.key === 'Enter' && !event.isComposing) {
    event.preventDefault()

    if (navigation === 'item') {
      activeVisibleItem(dropdown)?.click()
    }
  }

  return navigation
}

export const setupDropdownSearch = (
  dropdown: HTMLElement
): DropdownSearchController => {
  let state: SearchState = { type: 'absent' }
  let resultSources: ReadonlyMap<HTMLElement, WaDropdownItem> = new Map()

  const syncResults = (query: string): void => {
    resultSources = applySearch(dropdown, query)
  }

  const searchResultFromEvent = (event: MouseEvent): HTMLElement | undefined =>
    pipe(
      event.composedPath(),
      find(
        (target): target is HTMLElement =>
          target instanceof HTMLElement &&
          target.hasAttribute(SEARCH_RESULT_ATTRIBUTE)
      )
    )

  const itemPath = (item: WaDropdownItem): readonly WaDropdownItem[] => {
    const parent = item.parentElement

    return parent !== dropdown && parent !== null && isDropdownItem(parent)
      ? [item, ...itemPath(parent)]
      : [item]
  }

  const activateResult = (item: WaDropdownItem): void => {
    if (
      item.hasAttribute('disabled') &&
      !item.hasAttribute(SEARCH_DISABLED_ATTRIBUTE)
    ) {
      return
    }

    const searchDisabledItems = pipe(
      itemPath(item),
      filter((pathItem) => pathItem.hasAttribute(SEARCH_DISABLED_ATTRIBUTE))
    )

    pipe(
      searchDisabledItems,
      forEach((pathItem) => pathItem.removeAttribute('disabled'))
    )
    item.click()
    pipe(
      searchDisabledItems,
      filter(
        (pathItem) =>
          pathItem.isConnected &&
          pathItem.hasAttribute(SEARCH_DISABLED_ATTRIBUTE)
      ),
      forEach((pathItem) => pathItem.setAttribute('disabled', ''))
    )
  }

  const handleResultClick = (event: MouseEvent): void => {
    const result = searchResultFromEvent(event)
    const source = result === undefined ? undefined : resultSources.get(result)

    if (source === undefined || result?.hasAttribute('disabled') === true) {
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
    activateResult(source)
  }

  const bindInput = (input: WaInput, query: string): SearchState => {
    input.value = query
    const nativeInput = input.input
    const handledInputEvents = new WeakSet<Event>()
    const handledKeydownEvents = new WeakSet<KeyboardEvent>()

    const handleInput = (event: Event): void => {
      if (
        handledInputEvents.has(event) ||
        !(state.type === 'bound' && state.input === input)
      ) {
        return
      }

      handledInputEvents.add(event)
      const nextQuery = searchInputValue(event, input)

      state = { ...state, navigation: 'idle', query: nextQuery }
      closeSubmenus(dropdown)
      clearActiveItems(dropdown)
      syncResults(nextQuery)
    }
    const handleKeydown = (event: KeyboardEvent): void => {
      if (handledKeydownEvents.has(event)) {
        return
      }

      handledKeydownEvents.add(event)
      const navigation =
        state.type === 'bound' && state.input === input
          ? state.navigation
          : 'idle'
      const nextNavigation = handleSearchKeydown(dropdown, event, navigation)

      if (state.type === 'bound' && state.input === input) {
        state = { ...state, navigation: nextNavigation }
      }
    }

    input.addEventListener('input', handleInput, true)
    nativeInput?.addEventListener('input', handleInput)
    input.addEventListener('keydown', handleKeydown)
    nativeInput?.addEventListener('keydown', handleKeydown)

    return {
      cleanup: () => {
        input.removeEventListener('input', handleInput, true)
        nativeInput?.removeEventListener('input', handleInput)
        input.removeEventListener('keydown', handleKeydown)
        nativeInput?.removeEventListener('keydown', handleKeydown)
      },
      input,
      navigation: 'idle',
      query,
      type: 'bound'
    }
  }

  const sync = (): void => {
    const nextInput = searchInput(dropdown)
    const previousInput = state.type === 'bound' ? state.input : undefined
    const previousQuery = state.type === 'bound' ? state.query : ''

    if (nextInput === undefined && state.type === 'absent') {
      return
    }

    if (nextInput === previousInput && state.type === 'bound') {
      syncResults(previousQuery)
      clearActiveItems(dropdown)
      state = { ...state, navigation: 'idle' }
      return
    }

    if (state.type === 'bound') {
      state.cleanup()
    }

    if (nextInput === undefined) {
      state = { type: 'absent' }
      clearActiveItems(dropdown)
      removeSearchResults(dropdown)
      restoreItems(dropdown)
      resultSources = new Map()
      return
    }

    state = bindInput(nextInput, previousQuery)
    syncResults(previousQuery)
    clearActiveItems(dropdown)
  }

  const reset = (): void => {
    if (state.type === 'bound') {
      state.input.value = ''
      state = { ...state, navigation: 'idle', query: '' }
    }

    closeSubmenus(dropdown)
    clearActiveItems(dropdown)
    removeSearchResults(dropdown)
    restoreItems(dropdown)
    resultSources = new Map()
  }

  dropdown.addEventListener('click', handleResultClick, true)
  sync()

  return {
    cleanup: () => {
      if (state.type === 'bound') {
        state.cleanup()
      }

      closeSubmenus(dropdown)
      clearActiveItems(dropdown)
      removeSearchResults(dropdown)
      restoreItems(dropdown)
      dropdown.removeEventListener('click', handleResultClick, true)
      resultSources = new Map()
      state = { type: 'absent' }
    },
    focus: () => {
      if (state.type === 'bound') {
        clearActiveItems(dropdown)
        state = { ...state, navigation: 'idle' }
        state.input.focus({ preventScroll: true })
      }
    },
    prepareForUpdate: () => {
      clearActiveItems(dropdown)
      removeSearchResults(dropdown)
      restoreItems(dropdown)
      resultSources = new Map()
    },
    reset,
    sync
  }
}
